import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Col } from '../../lib/utils';
import { formatAmount } from './lib/format';
import { requestGiftTossPayment } from './lib/toss';
import { useGiftPaymentOrder } from './hooks/useGiftPaymentOrder';
import PaymentHeader from '../MemberPayment/components/PaymentHeader';
import AgreementCheckbox from '../MemberPayment/components/AgreementCheckbox';
import StickyBottomButton from '../MemberPayment/components/StickyBottomButton';
import OrderSummaryCard from './components/OrderSummaryCard';
import RecipientSection from './components/RecipientSection';
import MessagePreview from './components/MessagePreview';
import AmountSection from './components/AmountSection';
import InvalidOrderScreen from './components/InvalidOrderScreen';
import PaymentStateScreen from './components/PaymentStateScreen';

const GiftPayMemberPage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const [isAgreed, setIsAgreed] = useState(false);
    const [isRequestingToss, setIsRequestingToss] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const { order, error, isLoading } = useGiftPaymentOrder(orderId, true);

    if (isLoading) {
        return <PaymentStateScreen title="주문 정보를 불러오고 있어요" />;
    }

    if (!order || order.role !== 'MEMBER') {
        return <InvalidOrderScreen message={error?.message} />;
    }

    // 결제 수단 선택은 토스 결제창 안에서 — 페이지는 주문 확인·동의만 담당
    const handlePay = async () => {
        if (!isAgreed || isRequestingToss || order.status !== 'PENDING_PAYMENT') return;
        setPaymentError(null);
        setIsRequestingToss(true);
        try {
            await requestGiftTossPayment({
                orderId: order.orderId,
                orderName: `${order.product.brand} ${order.product.name}${
                    (order.quantity ?? 1) > 1 ? ` × ${order.quantity}` : ''
                }`,
                amount: order.amounts.final,
                customerName: order.payerName,
            });
        } catch (requestError) {
            setPaymentError(
                requestError instanceof Error ? requestError.message : '결제창을 열지 못했어요. 다시 시도해 주세요.',
            );
            setIsRequestingToss(false);
        }
    };

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#F5F7FA' }}>
            <Col style={{ width: '100%', maxWidth: 480, minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
                <PaymentHeader title="감사 선물 결제" />

                <Col style={{ flex: 1, padding: '16px 16px 24px', gap: 12 }}>
                    <OrderSummaryCard order={order} />
                    <RecipientSection recipient={order.recipient} />
                    <MessagePreview message={order.message} />
                    <AmountSection
                        amounts={order.amounts}
                        discountLabel={`회원 할인 (${order.amounts.discountRate}%)`}
                        strikeListPrice
                        quantity={order.quantity ?? 1}
                        unitPrice={order.product.price}
                    />
                    {order.status !== 'PENDING_PAYMENT' && (
                        <div role="status" style={{ color: '#D32F2F', fontSize: 14, textAlign: 'center' }}>
                            현재 주문 상태({order.status})에서는 결제를 진행할 수 없어요.
                        </div>
                    )}
                    {paymentError && (
                        <div role="alert" style={{ color: '#D32F2F', fontSize: 14, textAlign: 'center' }}>
                            {paymentError}
                        </div>
                    )}
                    <AgreementCheckbox
                        checked={isAgreed}
                        onChange={setIsAgreed}
                        label="결제 진행 및 기프티콘 발송에 동의합니다"
                    />
                </Col>

                <StickyBottomButton
                    text={
                        isRequestingToss
                            ? '결제창 준비중...'
                            : `${formatAmount(order.amounts.final)}원 결제하기`
                    }
                    onClick={handlePay}
                    disabled={!isAgreed || isRequestingToss || order.status !== 'PENDING_PAYMENT'}
                />
            </Col>
        </Col>
    );
};

export default GiftPayMemberPage;
